// Corner-claim voice-confirmation state machine (round-27, 2026-08-31). User feedback: a claim
// that lands in voiceScore.ts's new 'dubious' confidence band — real signal, clear winner, just
// short of the auto-accept bar — shouldn't be silently assigned OR silently dropped. The table
// should ASK ("¿Eres Luuk?", by voice via services/tts_service.py's server-side neural TTS AND
// on-screen via components/ConfirmClaimPrompt.vue) and let a yes/no answer (voice OR a tap)
// settle it.
//
// States: idle -> awaiting(candidate, side, deadline) -> {confirmed | denied | timeout |
// cancelled} -> back to idle. Every terminal transition:
//  1. records the outcome onto the originating command's transcript entry (stt.ts's
//     recordSpeakerMatchForCommand), same as cornerClaim.ts already does for its own outcomes;
//  2. posts a VoiceConfirmationInteraction row (server/app/models.py) — the observability table
//     behind "how often are we asking, and how often are we wrong" (cornerClaim.ts's
//     postConfirmationInteractionBestEffort posts the 'auto'/high-band case; this module posts
//     every ask/answer case: confirmed/denied/timeout/cancelled);
//  3. calls the starter's onResolve callback, which owns the actual UI reaction (setSide +
//     navigate/glow + a screen-appropriate toast/popup) — this module stays screen-agnostic,
//     same "pure resolution, caller decides what to do" split cornerClaim.ts already uses.
//
// ONE confirmation in flight at a time, REPLACE policy (not ignore): a second corner_claim while
// already awaiting an answer cancels the pending one and starts fresh. Chosen over "ignore the
// second claim" because a corner claim means someone is standing at the table right now — the
// NEWER claim is more likely to reflect who's actually there than a stale pending ask, and
// leaving a stale ask blocking a fresher one would make the table feel unresponsive. Cancellation
// is also triggered by ANY other command arriving (App.vue's onSttCommand hub — a pending
// confirmation is a promise the table made about ONE specific ask; once the conversation has
// clearly moved on, that promise is stale) and by navigating away from the screen that started it
// (App.vue's nav.screen watch).
import { reactive } from 'vue'
import { recordSpeakerMatchForCommand, type SttCommandEvent, type SttLang } from '../stt'
import { voiceConfirmNameTtsUrl } from '../backend'
import { postConfirmationInteractionBestEffort } from './cornerClaim'
import type { CornerClaimResult } from './cornerClaim'
import type { Player, Side } from '../types'

/** ~10s, tunable — long enough to read a name and tap/say an answer at arm's length from the
 *  table, short enough that a claim nobody answers doesn't leave the prompt hanging visibly. */
export const DECISION_TIMEOUT_MS = 10_000

export type ConfirmResolution = 'confirmed' | 'denied' | 'timeout' | 'cancelled'

interface ConfirmStateShape {
  status: 'idle' | 'awaiting'
  candidate: Player | null
  side: Side | null
  color: 'red' | 'blue' | null
  confidencePct: number | null
  /** The heard command's language — components/ConfirmClaimPrompt.vue shows the question and
   *  Sí/No vs. Yes/No button labels in this language (es-ES first, EN fallback per requirement). */
  lang: SttLang | null
  /** Epoch ms — components/ConfirmClaimPrompt.vue animates a countdown against this. */
  deadlineTs: number | null
}

/** Read by components/ConfirmClaimPrompt.vue (mounted once in App.vue, like ToastStack). Only
 *  ever ONE confirmation live at a time — see the module docblock's replace policy. */
export const confirmState = reactive<ConfirmStateShape>({
  status: 'idle',
  candidate: null,
  side: null,
  color: null,
  confidencePct: null,
  lang: null,
  deadlineTs: null,
})

interface InFlight {
  cmd: SttCommandEvent
  screen: string
  confirm: NonNullable<CornerClaimResult['confirm']>
  confidencePct: number
  side: Side
  color: 'red' | 'blue'
  player: Player
  startedAt: number
  timer: ReturnType<typeof setTimeout>
  ttsOkPromise: Promise<boolean>
  onResolve: (resolution: ConfirmResolution, player: Player) => void
  // Round-28 (2026-08-31): which candidate-scope tier was applied (services/candidateScope.ts's
  // scopeLabel) and which command this ask is for — both flow into the observability row so
  // corner-claim and point-claim asks are distinguishable and "why this candidate pool" is
  // answerable per-row without re-deriving it from the caller's context.
  scope: string
  command: 'corner_claim' | 'point_claim'
}

let inFlight: InFlight | null = null

/** Fires the confirm-name TTS request and reports whether playback actually started — used only
 *  for the observability row (ttsOk), never blocks/gates the ask-answer flow itself (the on-
 *  screen prompt + voice grammar are live regardless of whether audio plays). A 204 (TTS
 *  unavailable server-side), a network failure, or a decode error all resolve false; nothing here
 *  ever throws outward. */
function attemptConfirmTts(playerId: number, lang: SttLang): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false
    const finish = (ok: boolean) => {
      if (settled) return
      settled = true
      resolve(ok)
    }
    try {
      const audio = new Audio(voiceConfirmNameTtsUrl(playerId, lang))
      audio.addEventListener('error', () => finish(false), { once: true })
      audio.play().then(
        () => finish(true),
        () => finish(false),
      )
    } catch {
      finish(false)
    }
    // Safety net — never leave the observability write waiting on a stalled <audio> element past
    // a sane bound (well inside DECISION_TIMEOUT_MS, so it never delays anything user-visible).
    setTimeout(() => finish(false), 4000)
  })
}

export interface StartConfirmationParams {
  cmd: SttCommandEvent
  /** 'home' | 'newMatch' — which screen's corner-claim handling triggered this ask, for the
   *  observability row only (same field cornerClaim.ts's 'auto' rows carry). */
  screen: string
  /** Must be the 'needs_confirmation' CornerClaimResult (player + confirm both set) — callers
   *  get this from services/cornerClaim.ts's resolveCornerClaim() (corner_claim) or construct
   *  the equivalent shape themselves (point_claim — see LiveMatch.vue's resolvePointClaim). */
  result: CornerClaimResult
  /** Round-28: which candidate-scope tier applied (result.scopeLabel is the natural source for
   *  corner_claim callers) and which command this ask is for — see InFlight's docblock. */
  scope: string
  command: 'corner_claim' | 'point_claim'
  /** Called exactly once, on the terminal resolution. 'confirmed' is the only outcome that
   *  should actually apply anything (setSide etc.) — 'denied'/'timeout'/'cancelled' are all
   *  "do nothing" per the standing false-negative-biased design; callers may still show a light
   *  denial acknowledgment, but must never assign on anything but 'confirmed'. */
  onResolve: (resolution: ConfirmResolution, player: Player) => void
}

export function startConfirmation(params: StartConfirmationParams): void {
  const { cmd, screen, result, scope, command, onResolve } = params
  if (result.outcome !== 'needs_confirmation' || !result.player || !result.confirm || result.confidencePct == null) return
  // Replace policy — see module docblock.
  if (inFlight) cancelConfirmation()

  const startedAt = Date.now()
  const timer = setTimeout(() => finish('timeout', null), DECISION_TIMEOUT_MS)
  inFlight = {
    cmd,
    screen,
    confirm: result.confirm,
    confidencePct: result.confidencePct,
    side: result.side,
    color: result.color,
    player: result.player,
    startedAt,
    timer,
    ttsOkPromise: attemptConfirmTts(result.player.id, cmd.lang),
    onResolve,
    scope,
    command,
  }
  confirmState.status = 'awaiting'
  confirmState.candidate = result.player
  confirmState.side = result.side
  confirmState.color = result.color
  confirmState.confidencePct = result.confidencePct
  confirmState.lang = cmd.lang
  confirmState.deadlineTs = startedAt + DECISION_TIMEOUT_MS
}

function finish(resolution: ConfirmResolution, answerSource: 'voice' | 'tap' | null) {
  const cur = inFlight
  if (!cur) return
  clearTimeout(cur.timer)
  inFlight = null
  confirmState.status = 'idle'
  confirmState.candidate = null
  confirmState.side = null
  confirmState.color = null
  confirmState.confidencePct = null
  confirmState.lang = null
  confirmState.deadlineTs = null

  const latencyMs = resolution === 'cancelled' ? null : resolution === 'timeout' ? DECISION_TIMEOUT_MS : Date.now() - cur.startedAt

  recordSpeakerMatchForCommand(cur.cmd, {
    playerId: cur.player.id,
    displayName: cur.player.displayName,
    confidencePct: cur.confidencePct,
    outcome: resolution,
    scope: cur.scope,
  })

  void cur.ttsOkPromise.then((ttsOk) => {
    postConfirmationInteractionBestEffort({
      candidatePlayerId: cur.player.id,
      side: cur.side,
      color: cur.color,
      rawCosine: cur.confirm.rawCosine,
      calibratedConfidence: cur.confidencePct / 100,
      runnerUpPlayerId: cur.confirm.runnerUp?.id ?? null,
      runnerUpCosine: cur.confirm.runnerUp?.cosine ?? null,
      margin: cur.confirm.marginPct / 100,
      utteranceText: cur.cmd.raw,
      utteranceLang: cur.cmd.lang,
      utteranceDurationSec: cur.confirm.durationSec,
      resolution,
      answerSource,
      latencyMs,
      screen: cur.screen,
      ttsAttempted: true,
      ttsOk,
      scope: cur.scope,
      command: cur.command,
    })
  })

  cur.onResolve(resolution, cur.player)
}

/** Called by App.vue's onSttCommand hub on a 'yes_no' command while awaiting (no-op otherwise —
 *  see that hub's docblock for the "bare no elsewhere does nothing" gating) and by
 *  ConfirmClaimPrompt.vue's Sí/No buttons (source: 'tap' — voice must never be the only way out,
 *  per the requirement). */
export function resolveConfirmation(answer: 'yes' | 'no', source: 'voice' | 'tap'): void {
  if (!inFlight) return
  finish(answer === 'yes' ? 'confirmed' : 'denied', source)
}

/** Called on replace (a new corner_claim superseding this one), on any other command arriving
 *  while awaiting, and on navigating away from the screen that started it. No-op when nothing is
 *  in flight. */
export function cancelConfirmation(): void {
  if (!inFlight) return
  finish('cancelled', null)
}
